import { useState } from 'react'
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query'
import { useToast } from '../../components/Toast'
import scoresService from '../../services/scoresService'
import attitudesService from '../../services/attitudesService'
import { MonthlyScore, AttitudeCategory } from '../../services/types'
import { Tabs } from '../../components/Tabs'
import { AttitudeCard } from '../../components/AttitudeCard'
import { ConfirmModal } from '../../components/ConfirmModal'

const MONTH_NAMES = [
  'Janeiro', 'Fevereiro', 'Março', 'Abril', 'Maio', 'Junho',
  'Julho', 'Agosto', 'Setembro', 'Outubro', 'Novembro', 'Dezembro',
]

export const ApprovalsPage = () => {
  const toast = useToast()
  const queryClient = useQueryClient()

  // State
  const [selectedScore, setSelectedScore] = useState<MonthlyScore | null>(null)
  const [showRejectModal, setShowRejectModal] = useState(false)
  const [showChangesModal, setShowChangesModal] = useState(false)
  const [rejectReason, setRejectReason] = useState('')
  const [changesActiveTab, setChangesActiveTab] = useState<'positive' | 'negative'>('positive')
  const [changesAttitudes, setChangesAttitudes] = useState<Set<number>>(new Set())
  const [changesObservation, setChangesObservation] = useState('')
  const [reviewerObs, setReviewerObs] = useState('')

  // Fetch pending scores
  const { data: pendingScores = [], isLoading } = useQuery({
    queryKey: ['pending-scores'],
    queryFn: () => scoresService.getAll({ is_reversed: false }).then(
      scores => scores.filter(s => s.status === 'pending')
    ),
  })

  // Fetch attitudes
  const { data: attitudes = [] } = useQuery({
    queryKey: ['attitudes'],
    queryFn: () => attitudesService.getAll(),
  })

  const positiveAttitudes = attitudes
    .filter((a) => a.type === 'positive')
    .sort((a, b) => a.display_order - b.display_order)

  const negativeAttitudes = attitudes
    .filter((a) => a.type === 'negative')
    .sort((a, b) => a.display_order - b.display_order)

  // Review mutation
  const reviewMutation = useMutation({
    mutationFn: ({ id, data }: { id: number; data: Parameters<typeof scoresService.review>[1] }) =>
      scoresService.review(id, data),
    onSuccess: (_, variables) => {
      const action = variables.data.action
      const msg = action === 'approve'
        ? 'Avaliação aprovada com sucesso!'
        : action === 'approve_with_changes'
        ? 'Avaliação aprovada com ressalvas!'
        : 'Avaliação rejeitada.'
      toast.success(msg)
      queryClient.invalidateQueries({ queryKey: ['pending-scores'] })
      queryClient.invalidateQueries({ queryKey: ['pending-count'] })
      closeAllModals()
    },
    onError: (error: any) => {
      toast.error(
        error.response?.data?.error ||
        error.response?.data?.detail ||
        'Erro ao revisar avaliação.'
      )
    },
  })

  const closeAllModals = () => {
    setSelectedScore(null)
    setShowRejectModal(false)
    setShowChangesModal(false)
    setRejectReason('')
    setReviewerObs('')
    setChangesAttitudes(new Set())
    setChangesObservation('')
  }

  const handleApprove = (score: MonthlyScore) => {
    reviewMutation.mutate({
      id: score.id,
      data: { action: 'approve' },
    })
  }

  const openRejectModal = (score: MonthlyScore) => {
    setSelectedScore(score)
    setRejectReason('')
    setShowRejectModal(true)
  }

  const handleReject = () => {
    if (!selectedScore) return
    if (!rejectReason.trim()) {
      toast.warning('Informe o motivo da rejeição.')
      return
    }
    reviewMutation.mutate({
      id: selectedScore.id,
      data: {
        action: 'reject',
        reviewer_observation: rejectReason.trim(),
      },
    })
  }

  const openChangesModal = (score: MonthlyScore) => {
    setSelectedScore(score)
    setChangesAttitudes(new Set(score.selected_attitudes))
    setChangesObservation(score.observation || '')
    setReviewerObs('')
    setChangesActiveTab('positive')
    setShowChangesModal(true)
  }

  const toggleChangesAttitude = (id: number) => {
    const newSelected = new Set(changesAttitudes)
    if (newSelected.has(id)) {
      newSelected.delete(id)
    } else {
      newSelected.add(id)
    }
    setChangesAttitudes(newSelected)
  }

  const calculateChangesPoints = (): number => {
    const selected = Array.from(changesAttitudes)
      .map((id) => attitudes.find((a) => a.id === id))
      .filter(Boolean) as AttitudeCategory[]

    const positives = selected.filter((a) => a.type === 'positive')
    const highestPositive = positives.length > 0
      ? Math.max(...positives.map((a) => a.points))
      : 0

    const negatives = selected.filter((a) => a.type === 'negative')
    const totalNegative = negatives.reduce((sum, a) => sum + a.points, 0)

    return highestPositive + totalNegative
  }

  const handleApproveWithChanges = () => {
    if (!selectedScore) return
    if (changesAttitudes.size === 0) {
      toast.warning('Selecione ao menos uma atitude.')
      return
    }
    reviewMutation.mutate({
      id: selectedScore.id,
      data: {
        action: 'approve_with_changes',
        selected_attitudes: Array.from(changesAttitudes),
        observation: changesObservation.trim() || undefined,
        reviewer_observation: reviewerObs.trim() || undefined,
      },
    })
  }

  const changesPoints = calculateChangesPoints()

  return (
    <div>
      {/* Header */}
      <div className="mb-6">
        <h1 className="text-3xl font-bold text-gray-900 dark:text-white mb-2">
          Avaliações Pendentes
        </h1>
        <p className="text-gray-600 dark:text-gray-400">
          {pendingScores.length} avaliação(ões) aguardando revisão
        </p>
      </div>

      {/* Loading */}
      {isLoading ? (
        <div className="text-center py-12">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary mx-auto mb-4" />
          <p className="text-gray-600 dark:text-gray-400">Carregando avaliações pendentes...</p>
        </div>
      ) : pendingScores.length === 0 ? (
        <div className="bg-white dark:bg-gray-800 rounded-card shadow p-12 text-center">
          <div className="text-5xl mb-4">✓</div>
          <p className="text-gray-600 dark:text-gray-400 text-lg">
            Nenhuma avaliação pendente no momento.
          </p>
        </div>
      ) : (
        <div className="space-y-4">
          {pendingScores.map((score) => (
            <div
              key={score.id}
              className="bg-white dark:bg-gray-800 rounded-card shadow p-6 border border-yellow-200 dark:border-yellow-800"
            >
              <div className="flex flex-col md:flex-row md:items-center justify-between gap-4">
                {/* Employee Info */}
                <div className="flex items-center gap-4">
                  <div className="w-12 h-12 rounded-full bg-primary/10 flex items-center justify-center text-primary font-bold text-lg">
                    {score.employee_detail?.full_name?.charAt(0) || '?'}
                  </div>
                  <div>
                    <h3 className="font-semibold text-gray-900 dark:text-white">
                      {score.employee_detail?.full_name}
                    </h3>
                    <p className="text-sm text-gray-500 dark:text-gray-400">
                      {score.employee_detail?.primary_company_detail?.name} — {MONTH_NAMES[score.month - 1]}/{score.year}
                    </p>
                  </div>
                </div>

                {/* Points */}
                <div className="text-right">
                  <p className="text-2xl font-bold text-gray-900 dark:text-white">
                    {score.points_snapshot > 0 ? '+' : ''}{score.points_snapshot} pts
                  </p>
                  <p className="text-xs text-gray-500 dark:text-gray-400">
                    Enviado em {score.submitted_at ? new Date(score.submitted_at).toLocaleDateString('pt-BR') : '-'}
                  </p>
                </div>
              </div>

              {/* Attitudes */}
              <div className="mt-4">
                <p className="text-sm font-medium text-gray-600 dark:text-gray-400 mb-2">Atitudes selecionadas:</p>
                <div className="flex flex-wrap gap-1.5">
                  {score.selected_attitudes_detail?.map((att) => (
                    <span
                      key={att.id}
                      className={`inline-flex px-2.5 py-1 rounded-full text-xs font-medium ${
                        att.type === 'positive'
                          ? 'bg-green-100 dark:bg-green-900/30 text-green-700 dark:text-green-400'
                          : 'bg-red-100 dark:bg-red-900/30 text-red-700 dark:text-red-400'
                      }`}
                    >
                      {att.name} ({att.points > 0 ? '+' : ''}{att.points})
                    </span>
                  ))}
                </div>
              </div>

              {/* Observation */}
              {score.observation && (
                <div className="mt-3 p-3 bg-gray-50 dark:bg-gray-700/50 rounded-btn">
                  <p className="text-sm text-gray-700 dark:text-gray-300">
                    <span className="font-medium">Obs do funcionário:</span> {score.observation}
                  </p>
                </div>
              )}

              {/* Actions */}
              <div className="mt-4 flex gap-3 justify-end">
                <button
                  onClick={() => handleApprove(score)}
                  disabled={reviewMutation.isPending}
                  className="px-4 py-2 bg-green-600 hover:bg-green-700 text-white font-medium rounded-btn transition-colors disabled:opacity-50"
                >
                  Aprovar
                </button>
                <button
                  onClick={() => openChangesModal(score)}
                  disabled={reviewMutation.isPending}
                  className="px-4 py-2 bg-yellow-500 hover:bg-yellow-600 text-white font-medium rounded-btn transition-colors disabled:opacity-50"
                >
                  Ressalvas
                </button>
                <button
                  onClick={() => openRejectModal(score)}
                  disabled={reviewMutation.isPending}
                  className="px-4 py-2 border border-danger text-danger hover:bg-danger hover:text-white font-medium rounded-btn transition-colors disabled:opacity-50"
                >
                  Negar
                </button>
              </div>
            </div>
          ))}
        </div>
      )}

      {/* Reject Modal */}
      <ConfirmModal
        isOpen={showRejectModal}
        onClose={() => { setShowRejectModal(false); setSelectedScore(null) }}
        onConfirm={handleReject}
        title="Rejeitar Avaliação"
        description={
          <div className="space-y-3">
            <p>
              Rejeitar a autoavaliação de <strong>{selectedScore?.employee_detail?.full_name}</strong>?
            </p>
            <div>
              <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                Motivo da rejeição *
              </label>
              <textarea
                value={rejectReason}
                onChange={(e) => setRejectReason(e.target.value)}
                rows={3}
                className="w-full px-4 py-2 border border-gray-300 dark:border-gray-600 rounded-btn bg-white dark:bg-gray-700 text-gray-900 dark:text-white focus:ring-2 focus:ring-primary focus:border-transparent resize-none"
                placeholder="Explique o motivo para o funcionário..."
              />
            </div>
          </div>
        }
        confirmText="Rejeitar"
        cancelText="Cancelar"
        isDanger
        isLoading={reviewMutation.isPending}
      />

      {/* Changes Modal (Approve with Changes) */}
      {showChangesModal && selectedScore && (
        <>
          <div className="fixed inset-0 bg-black/50 z-40" onClick={closeAllModals} />
          <div className="fixed inset-0 z-50 flex items-center justify-center p-4">
            <div className="bg-white dark:bg-gray-800 rounded-card shadow-xl max-w-4xl w-full p-6 max-h-[90vh] overflow-y-auto">
              <h3 className="text-xl font-semibold text-gray-900 dark:text-white mb-2">
                Aprovar com Ressalvas
              </h3>
              <p className="text-sm text-gray-500 dark:text-gray-400 mb-6">
                {selectedScore.employee_detail?.full_name} — {MONTH_NAMES[selectedScore.month - 1]}/{selectedScore.year}
              </p>

              {/* Points comparison */}
              <div className="grid grid-cols-2 gap-4 mb-6">
                <div className="bg-gray-50 dark:bg-gray-700/50 rounded-btn p-4 text-center">
                  <p className="text-sm text-gray-500 dark:text-gray-400">Original</p>
                  <p className="text-2xl font-bold text-gray-900 dark:text-white">
                    {selectedScore.points_snapshot > 0 ? '+' : ''}{selectedScore.points_snapshot} pts
                  </p>
                </div>
                <div className="bg-primary/10 rounded-btn p-4 text-center">
                  <p className="text-sm text-gray-500 dark:text-gray-400">Com alterações</p>
                  <p className="text-2xl font-bold text-gray-900 dark:text-white">
                    {changesPoints > 0 ? '+' : ''}{changesPoints} pts
                  </p>
                </div>
              </div>

              {/* Tabs */}
              <div className="mb-4">
                <Tabs
                  tabs={[
                    { id: 'positive', label: 'Positivo', icon: '✓' },
                    { id: 'negative', label: 'Negativo', icon: '✗' },
                  ]}
                  activeTab={changesActiveTab}
                  onTabChange={(tab) => setChangesActiveTab(tab as 'positive' | 'negative')}
                />
              </div>

              {/* Attitudes Grid */}
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-6">
                {changesActiveTab === 'positive'
                  ? positiveAttitudes.map((attitude) => (
                      <AttitudeCard
                        key={attitude.id}
                        attitude={attitude}
                        isSelected={changesAttitudes.has(attitude.id)}
                        onToggle={toggleChangesAttitude}
                      />
                    ))
                  : negativeAttitudes.map((attitude) => (
                      <AttitudeCard
                        key={attitude.id}
                        attitude={attitude}
                        isSelected={changesAttitudes.has(attitude.id)}
                        onToggle={toggleChangesAttitude}
                      />
                    ))}
              </div>

              {/* Observation */}
              <div className="mb-4">
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                  Observação da avaliação
                </label>
                <textarea
                  value={changesObservation}
                  onChange={(e) => setChangesObservation(e.target.value)}
                  rows={2}
                  className="w-full px-4 py-2 border border-gray-300 dark:border-gray-600 rounded-btn bg-white dark:bg-gray-700 text-gray-900 dark:text-white focus:ring-2 focus:ring-primary focus:border-transparent resize-none"
                  placeholder="Observação sobre a avaliação..."
                />
              </div>

              {/* Reviewer Observation */}
              <div className="mb-6">
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                  Observação do revisor (opcional)
                </label>
                <textarea
                  value={reviewerObs}
                  onChange={(e) => setReviewerObs(e.target.value)}
                  rows={2}
                  className="w-full px-4 py-2 border border-gray-300 dark:border-gray-600 rounded-btn bg-white dark:bg-gray-700 text-gray-900 dark:text-white focus:ring-2 focus:ring-primary focus:border-transparent resize-none"
                  placeholder="Motivo das alterações..."
                />
              </div>

              {/* Actions */}
              <div className="flex gap-3 justify-end border-t border-gray-200 dark:border-gray-700 pt-4">
                <button
                  type="button"
                  onClick={closeAllModals}
                  disabled={reviewMutation.isPending}
                  className="px-4 py-2 border border-gray-300 dark:border-gray-600 rounded-btn text-gray-700 dark:text-gray-300 hover:bg-gray-100 dark:hover:bg-gray-700 transition-colors"
                >
                  Cancelar
                </button>
                <button
                  onClick={handleApproveWithChanges}
                  disabled={reviewMutation.isPending || changesAttitudes.size === 0}
                  className="px-6 py-2 bg-yellow-500 hover:bg-yellow-600 text-white font-medium rounded-btn transition-colors disabled:opacity-50"
                >
                  {reviewMutation.isPending ? 'Salvando...' : 'Aprovar com Ressalvas'}
                </button>
              </div>
            </div>
          </div>
        </>
      )}
    </div>
  )
}

export default ApprovalsPage
